/**
 * Rule registry record. Plan §26.5.
 *
 * A `RuleRecord` is the canonical persisted form of a regulatory rule, before
 * any vehicle config is overlaid. Cards (`card.ts`) are computed views derived
 * from rules + a vehicle config + an evidence index. Rules are the truth,
 * cards are the per-vehicle projection.
 *
 * `conditionExpr` is a string in the deterministic DSL evaluated by
 * `packages/dsl-evaluator` (W7). NEVER `new Function`, NEVER `eval`. Optional
 * `customEvaluators` lists names referenced inside the DSL; CI red line
 * (future) will reject custom-evaluator names that aren't registered.
 */

import { z } from "zod";
import {
  RegulatoryDomain,
  ObligationKind,
  ObligationTiming,
  LegalEffect,
  LifecycleState,
  VerificationMaturityTier,
} from "./primitives";
import {
  LegalReferenceSchema,
  EvidenceRequirementSchema,
} from "./legal-reference";
import { ContinuingObligationSchema } from "./continuing-obligation";

export const RuleConditionExprSchema = z.string().min(1);

export const RuleRecordSchema = z.object({
  ruleId: z.string().min(1),
  title: z.string().min(1),
  family: z.string(),
  jurisdictionCode: z.string().min(1),
  regulatoryDomain: RegulatoryDomain,
  obligationKind: ObligationKind,
  obligationTiming: ObligationTiming,
  legalEffect: LegalEffect,
  legalReferences: z.array(LegalReferenceSchema),
  evidenceGuidance: z.array(EvidenceRequirementSchema),
  conditionExpr: RuleConditionExprSchema,
  customEvaluators: z.array(z.string()).optional(),
  requirementText: z.string(),
  continuingObligation: ContinuingObligationSchema.optional(),
  lifecycleState: LifecycleState,
  verificationMaturityTier: VerificationMaturityTier,
  contractVersion: z.string(),
  schemaVersion: z.string(),
  registrySnapshotId: z.string(),
  sourceUri: z.string().url(),
  sourceSnapshotDate: z.string(),
  parserVersion: z.string(),
});
export type RuleRecord = z.infer<typeof RuleRecordSchema>;
